import { useState, useEffect } from "react";
import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { useResizeObserver } from "@react-hookz/web";
import cx from "classnames";
import {
  useMotionValue,
  useAnimate,
  AnimationOptions,
  MotionValue,
  ObjectTarget,
} from "motion/react";
import { RichText } from "@components/shared/core/RichText";
import { ThemeContextProvider } from "@components/providers/ThemeContextProvider";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { Slide } from "./";
import { browserIsSafari } from "@utils/browserIsSafari";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { EmbeddedAsteriskHighlight } from "./EmbeddedAsteriskHighlight";

type TProps = {
  index: number;
  slideWidth: number;
  slideHeight: number;
  gap: number;
  slide: Slide;
  offset: number;
  totalSlides: number;
  direction: 1 | -1 | 0;
  animationType: AnimationOptions;
  setAnimationType: (t: AnimationOptions) => void;
};

const CarouselItem = ({
  index,
  slideWidth,
  slideHeight,
  gap,
  slide,
  offset,
  totalSlides,
  direction,
  animationType,
  setAnimationType,
}: TProps) => {
  const isDesktop = useIsDesktop();
  const [scope, animate] = useAnimate();
  const [slideOffset, setSlideOffset] = useState(0);
  const curveRatio = isDesktop ? 0.85 : 0.95; // curve ratio
  const totalWidth = slideWidth + gap;
  const relativeIndex =
    (index * totalWidth - offset + slideOffset) / totalWidth;
  const xPosition = useMotionValue(0);
  const yPosition = useMotionValue(-100);
  const rotate = useMotionValue(0);
  const backgroundSize = useMotionValue("120%");
  const textPosition = useMotionValue(0);
  const isActive = relativeIndex === 0;
  const animatedSplitLinesMv = useMotionValue(-1);
  useEffect(() => {
    animate(animatedSplitLinesMv, isActive ? 0 : -1, animationCurves.long);
  }, [isActive, animatedSplitLinesMv, animate]);

  useResizeObserver(document.documentElement, () => {
    setSlideOffset(0);
  });

  useEffect(() => {
    getSlideStyle(index);
  });

  useEffect(() => {
    if (relativeIndex === 0) {
      animate(textPosition, 0, animationCurves.long);
    } else {
      animate(textPosition, -1, animationCurves.long);
    }
  }, [relativeIndex, animate, textPosition]);

  // Calculate each slide position
  const getSlideStyle = (index: number) => {
    const x = index * totalWidth - offset;
    if (direction === 1 && relativeIndex < -Math.round(totalSlides / 2)) {
      setSlideOffset((prev) => {
        xPosition.set(totalSlides * totalWidth + prev + x, false);
        return totalSlides * totalWidth + prev;
      });
    }
    if (direction === -1 && relativeIndex > Math.round(totalSlides / 2)) {
      setSlideOffset((prev) => {
        xPosition.set(prev - totalSlides * totalWidth + x, false);
        return prev - totalSlides * totalWidth;
      });
    }
    const adjustedX = x + slideOffset;

    const newBackgroundSize = relativeIndex === 0 ? "120%" : "170%";
    const yDisplacement = Math.abs(adjustedX) * (1 - curveRatio) - 250;
    const y =
      relativeIndex >= -1 && relativeIndex <= 1
        ? yDisplacement
        : yDisplacement + Math.abs(relativeIndex * relativeIndex * 50); // Arc height

    animate(xPosition, adjustedX as ObjectTarget<MotionValue<number>>, {
      ...animationType,
      onComplete: () => {
        // This is used for dragging effect to be more "realistic",
        // but after the animation ends, we go back to the standard animation
        setAnimationType(animationCurves.long);
      },
    });
    const rotationAngle = isDesktop ? 15 : 5;
    animate(yPosition, y as ObjectTarget<MotionValue<number>>, animationType);
    animate(
      rotate,
      (relativeIndex * rotationAngle) as ObjectTarget<MotionValue<number>>,
      animationType
    );
    animate(
      backgroundSize,
      newBackgroundSize as ObjectTarget<MotionValue<string>>,
      animationType
    );
  };

  const sourceInfoOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <p className="text-brand-cream text-label font-normal mt-2 dsk:mt-5">
          {children}
        </p>
      ),
    },
  } satisfies Options;

  return (
    <ThemeContextProvider
      theme={slide.highlightTheme!}
      ref={scope}
      className="absolute top-1/2 flex flex-col justify-between rounded-xl bg-no-repeat bg-brand-cream/25 p-6 dsk:p-12 transition-[filter] duration-long"
      style={{
        width: `${slideWidth}px`,
        height: `${slideHeight}px`,
        margin: `0 ${gap / 2}px`,
        x: xPosition,
        y: yPosition,
        rotate,
        filter:
          isActive && !browserIsSafari ? "drop-shadow(0px 0px 300px)" : "",
      }}
    >
      {/* Active / inactive overlay */}
      <div
        className={cx(
          "absolute top-0 left-0 w-full h-full rounded-xl z-20 transition-colors",
          isActive ? "bg-palette-current-highlight/15" : "bg-brand-black/50"
        )}
      />
      <div>
        {!!slide.icon && (
          <div
            className="w-12 h-12 bg-palette-current-highlight"
            style={{
              mask: `url(${slide.icon}) no-repeat center`,
            }}
          />
        )}
      </div>
      <div className="my-auto dsk:my-0">
        <p
          className="text-heading-4 dsk:text-heading-3 !text-neutral-light"
          style={{
            lineHeight: "calc(1.1 * var(--locale-scaling-line-height, 1))",
          }}
        >
          <AnimatedSplitLines
            value={animatedSplitLinesMv}
            animationType="clipPath"
          >
            <EmbeddedAsteriskHighlight>
              {slide.content}
            </EmbeddedAsteriskHighlight>
          </AnimatedSplitLines>
        </p>
        {!!slide.sourceInfo && (
          <div
            className="relative z-30 transition duration-long"
            style={{
              opacity: isActive ? 1 : 0,
            }}
          >
            <RichText content={slide.sourceInfo} options={sourceInfoOptions} />
          </div>
        )}
      </div>
    </ThemeContextProvider>
  );
};

export default CarouselItem;
